/* Helix Health Series A — Project Solstice
 * Loads the eval-set JSON files and normalizes them to the App's runtime shapes.
 *
 * Source files were copied verbatim from
 *   eval-sets/series-a-healthtech/{corpus,workstreams,expected-per-email}.json
 * The Helix corpus uses human-label doc_type strings ("Engagement Letter") and
 * "v1"-style versions; the app's renderer keys off the lowercase taxonomy.
 */
import corpusJson from "./helix-corpus.json";
import workstreamsJson from "./helix-workstreams.json";
import expectedJson from "./helix-expected.json";
import finalStateJson from "./helix-final-state.json";

const DOC_TYPE_NORMALIZE = {
  "engagement letter": "engagement_letter",
  "term sheet": "term_sheet",
  "stock purchase agreement": "spa",
  "spa": "spa",
  "certificate of incorporation": "charter",
  "charter": "charter",
  "amended and restated certificate of incorporation": "charter",
  "investors rights agreement": "ira",
  "investors' rights agreement": "ira",
  "ira": "ira",
  "rofr / co-sale agreement": "rofr",
  "rofr": "rofr",
  "right of first refusal and co-sale": "rofr",
  "voting agreement": "voting_agreement",
  "side letter": "side_letter",
  "board consent": "board_consent",
  "stockholder consent": "board_consent",
  "cap table": "cap_table",
  "opinion letter": "opinion_letter",
  "legal opinion": "opinion_letter",
  "nda": "nda",
  "non-disclosure agreement": "nda",
  "disclosure schedule": "disclosure_schedule",
  "closing checklist": "closing_checklist",
  "funds flow": "other",
  "officer's certificate": "other",
  "secretary's certificate": "other",
  "compliance certificate": "other",
  "certificate of correction": "charter",
};
function normDocType(t) {
  if (!t) return "other";
  const k = String(t).toLowerCase().trim();
  return DOC_TYPE_NORMALIZE[k] || (k.includes("term sheet") ? "term_sheet"
    : k.includes("charter") ? "charter"
    : k.includes("spa") || k.includes("purchase agreement") ? "spa"
    : k.includes("ira") || k.includes("rights agreement") ? "ira"
    : k.includes("voting") ? "voting_agreement"
    : k.includes("rofr") || k.includes("co-sale") ? "rofr"
    : k.includes("side letter") ? "side_letter"
    : k.includes("board") || k.includes("consent") ? "board_consent"
    : k.includes("cap table") ? "cap_table"
    : k.includes("opinion") ? "opinion_letter"
    : k.includes("engagement") ? "engagement_letter"
    : k.includes("nda") ? "nda"
    : k.includes("disclosure schedule") ? "disclosure_schedule"
    : k.includes("closing checklist") ? "closing_checklist"
    : "other");
}
function normVersion(v) {
  if (v == null) return 1;
  if (typeof v === "number") return v;
  /* Decimal point-releases are real versions here ("v3.1" supersedes "v3"). */
  const m = String(v).match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 1;
}

/* Corpus email ids are mixed: 1..32 are integers, E33..E102 are "E##" strings.
 * The runtime keys the eval expected-map by integer (parsed from the expected
 * file's "E##" ids), and approval keys / catalog state all assume a stable id.
 * Normalize every email id to the integer the expected-map uses. */
function normEmailId(rid) {
  if (typeof rid === "number") return rid;
  const m = String(rid).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : rid;
}

/* Check thresholds (mirror of App.jsx CHECKS τ). A risk check firing at/above τ
 * is what the spine surfaces to the lawyer as an approval. */
const RISK_TAU = { conflict: 0.55, newpart: 0.75, phase: 0.7, anomaly: 0.6, privilege: 0.7 };
/* check-id → [approval-src, approval-type] (matches the array-form authored approvals) */
const RISK_APPROVAL = {
  conflict: ["conflict", "conflict"],
  newpart: ["newpart", "participant"],
  phase: ["phase", "phase"],
  anomaly: ["anomaly", "anomaly"],
  privilege: ["privilege", "privilege"],
};

/* Part of the corpus (E33..E102) authored approvals as {label} objects, which the
 * app's array-indexing (a[0]/a[1]) can't read, so those approvals never surface.
 * Normalize r.approvals into the array schema the spine expects:
 *   - keep any already-array approvals (hand-authored, carry conflict options/detail)
 *   - drop {label}-only objects, but reuse their text
 *   - synthesize a typed approval for each risk check that fired ≥ τ and isn't
 *     already represented, so the issue surfaces exactly as the checks detected it. */
function normalizeResult(r) {
  if (!r) return r;
  const checks = r.checks || [];
  const arrApprovals = (r.approvals || []).filter((a) => Array.isArray(a));
  const dictLabels = (r.approvals || [])
    .filter((a) => a && !Array.isArray(a) && a.label)
    .map((a) => a.label);
  const haveType = new Set(arrApprovals.map((a) => a[1]));
  const out = [...arrApprovals];
  let li = 0;
  checks.forEach((c) => {
    const map = RISK_APPROVAL[c.id];
    if (!map) return;
    if (typeof c.conf === "number" && c.conf < RISK_TAU[c.id]) return;
    if (haveType.has(map[1])) return; // already surfaced by a hand-authored approval
    haveType.add(map[1]);
    const label = dictLabels[li++] || c.out || (map[1] + " check fired");
    out.push([map[0], map[1], label, c.out || "", false, null]);
  });
  return { ...r, approvals: out };
}

const emails = (corpusJson.emails || []).map((e) => ({
  ...e,
  id: normEmailId(e.id),
  r: normalizeResult(e.r),
  attachments: (e.attachments || []).map((a) => ({
    filename: a.filename,
    doc_type: normDocType(a.doc_type),
    version: normVersion(a.version),
    content: a.content || "",
  })),
}));

export const HELIX_CORPUS = {
  id: corpusJson.id || "corp_solstice",
  name: corpusJson.name || "Helix Health Series A — Project Solstice",
  note: corpusJson.note || "$40M Series A · Helix Health · Cascade Bio Partners lead · 102 emails over 12 weeks with planted complications across every check.",
  updated: corpusJson.updated || "Jun 4, 2026",
  emails,
  log: corpusJson.log || ["Imported from eval-sets/series-a-healthtech"],
};

export const HELIX_BUNDLE = {
  id: workstreamsJson.id || "wsb_solstice",
  name: workstreamsJson.name || "Helix Health Series A — working groups",
  note: workstreamsJson.note || "Project Solstice — Series A financing + Pham IP release + Atrium BD pipeline",
  createdAt: workstreamsJson.createdAt || "May 4, 2026",
  workstreams: workstreamsJson.workstreams || [],
  log: workstreamsJson.log || ["Imported from eval-sets/series-a-healthtech"],
};

/* Expected file uses "E1"-style email_id and `checks_should_fire`; the runtime
 * eval key is the integer corpus email id with flat booleans the spine compares
 * against (catalog, conflict, multi, privilege, newpart, phase, noise,
 * doc_expectations). Translate.
 */
function emailIdFromExpected(rawId) {
  if (typeof rawId === "number") return rawId;
  const m = String(rawId).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
const HELIX_EXPECTED = {};
(expectedJson.entries || []).forEach((entry) => {
  const id = emailIdFromExpected(entry.email_id);
  if (id == null) return;
  // An empty workstreams list means "unassociated" (firm admin / wrong matter /
  // BD-only spam). [] is truthy in JS, so guard on length too or these emails
  // score as catalog failures instead of correctly-routed noise.
  const wsRaw = (entry.catalog && entry.catalog.workstreams) || [];
  const wsList = wsRaw.length ? wsRaw : ["unassoc"];
  const fire = entry.checks_should_fire || {};
  const phaseFire = !!(fire.phase && (typeof fire.phase === "object" ? fire.phase.should_fire : fire.phase));
  HELIX_EXPECTED[id] = {
    catalog: wsList,
    conflict: !!fire.conflict,
    multi: wsList.length > 1,
    privilege: !!fire.privilege,
    newpart: !!fire.newpart,
    anomaly: !!fire.anomaly,
    phase: phaseFire,
    noise: wsList.length === 1 && wsList[0] === "unassoc",
    doc_expectations: ((entry.extract && entry.extract.doc_references) || []).map((d) => ({ filename: d })),
  };
});
export const HELIX_EVAL_EXPECTED = HELIX_EXPECTED;

/* The eval set is PAIRED with the corpus: it carries the ground-truth final
 * deal state (expected-final-state.json) plus per-email decision timelines
 * derived from it. The simulation replays these decisions during a run so the
 * deal memo ends up fully populated and matching the expected final state.
 *
 * Deal-memo matter M-3201 == ws_solstice (the Series A workstream). */
const MATTER_TO_WS = { "M-3201": "ws_solstice", "M-3202": "ws_helix_ip", "M-BD-104": "ws_atrium_bd" };
const memoWs = MATTER_TO_WS[(finalStateJson.deal_memo || {}).matter_code] || "ws_solstice";

/* memoTimeline: int emailId → [{ ws, key, value, source, final }]. One write per
 * history entry of each deal-memo field, in the corpus email order. The LAST write
 * of each field uses the field's final_value so the memo converges exactly on the
 * expected final state; intermediate writes preserve the change/override trail. */
const HELIX_MEMO_TIMELINE = {};
function pushMemoWrite(emailInt, w) {
  (HELIX_MEMO_TIMELINE[emailInt] = HELIX_MEMO_TIMELINE[emailInt] || []).push(w);
}
((finalStateJson.deal_memo || {}).fields || []).forEach((f) => {
  const hist = f.history || [];
  hist.forEach((h, idx) => {
    const eid = normEmailId(h.source_email);
    if (typeof eid !== "number") return;
    const isLast = idx === hist.length - 1;
    pushMemoWrite(eid, {
      ws: memoWs,
      key: f.key,
      value: isLast ? f.final_value : h.value,
      source: h.event || h.source_email,
      final: isLast,
    });
  });
});

/* phaseTimeline: int emailId → phase name (the transition target). */
const HELIX_PHASE_TIMELINE = {};
((finalStateJson.phase_track || {}).transitions || []).forEach((t) => {
  const eid = normEmailId(t.first_evidenced_email);
  if (typeof eid === "number") HELIX_PHASE_TIMELINE[eid] = { ws: memoWs, name: t.to };
});

export const HELIX_FINAL_STATE = finalStateJson;

export const HELIX_EVAL = {
  id: "eval_solstice",
  name: "Helix Series A — Project Solstice eval",
  corpusId: HELIX_CORPUS.id,
  when: "Jun 4, 2026",
  expected: HELIX_EVAL_EXPECTED,
  finalState: HELIX_FINAL_STATE,
  memoMatterWs: memoWs,
  memoTimeline: HELIX_MEMO_TIMELINE,
  phaseTimeline: HELIX_PHASE_TIMELINE,
};
